import { TCreateConverter, TFetchConverter } from 'data/types/converter.types';
import {
  EFamilyFilterParams,
  TEditFamilyDB,
  TEditFamilyUI,
  TFamilyDataDB,
  TFamilyDataUi,
  TFamilyExportRequest,
  TFamilyExportSort,
} from 'data/types/family.types';
import {
  EAgeGroup,
  TSortParams,
  TTableFilters,
} from 'data/types/generic.types';
import { FilterValue } from 'antd/es/table/interface';
import { phoneNumberConverter } from './general.converters';
import { workshopSessionDateConverter } from './workshops.convertors';
import { tagsGetConverter } from './tags.convertor';

export const getFamilyConvertor: TFetchConverter<TFamilyDataUi, TFamilyDataDB> =
  {
    fromDb: data => {
      return {
        registrantId: data.registrantId,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: phoneNumberConverter.fromDb(data.phone) || '',
        childrenNames: data.childrenNames,
        workshopClassId: data.workshopClassId,
        workshopId: data.workshopId,
        workshopType: data.workshopType,
        ageGroup: data.ageGroup,
        sessionDates: workshopSessionDateConverter.fromDb(data.sessionDates),
        location: data.location,
        tags: tagsGetConverter.fromDb(data.tags),
      };
    },
  };

export const familyFilterConverter = (
  filters: TTableFilters<EFamilyFilterParams>['filters'],
) => {
  const filtersToDB = {
    ...(filters?.firstName && {
      [EFamilyFilterParams.FIRST_NAME]: filters.firstName.toString().trim(),
    }),
    ...(filters?.lastName && {
      [EFamilyFilterParams.LAST_NAME]: filters.lastName.toString().trim(),
    }),
    ...(filters?.email && {
      [EFamilyFilterParams.EMAIL]: filters.email.toString().trim(),
    }),
    ...(filters?.phone && {
      [EFamilyFilterParams.MOBILE]: filters.phone.toString().trim(),
    }),
    ...(filters?.locationIds && {
      [EFamilyFilterParams.LOCATION]: filters?.locationIds,
    }),
    ...(filters?.workshopTypes && {
      [EFamilyFilterParams.TYPE]: filters?.workshopTypes.map(type => type),
    }),
    ...(filters?.ageGroups && {
      [EFamilyFilterParams.AGE_GROUP]: filters?.ageGroups,
    }),
    ...(filters?.tagIds && {
      [EFamilyFilterParams.TAGS]: filters?.tagIds,
    }),
  };
  return filtersToDB;
};

export const familyUpdateConverter: TCreateConverter<
  TEditFamilyUI,
  TEditFamilyDB
> = {
  toDb: data => ({
    firstName: data.firstName,
    lastName: data.lastName,
    email: data.email,
    phone: phoneNumberConverter.toDb(data.phone) || '',
    registrantId: data.registrantId,
    workshopClassId: data.workshopClassId,
    workshopId: data.workshopId,
  }),
};

const COLUMN_KEY_TO_EXPORT_ID: Record<string, string> = {
  [EFamilyFilterParams.FIRST_NAME]: 'firstName',
  [EFamilyFilterParams.LAST_NAME]: 'lastName',
  childrenNames: 'childrenNames',
  [EFamilyFilterParams.MOBILE]: 'phone',
  [EFamilyFilterParams.EMAIL]: 'email',
  [EFamilyFilterParams.LOCATION]: 'location',
  [EFamilyFilterParams.TYPE]: 'workshopType',
  [EFamilyFilterParams.AGE_GROUP]: 'ageGroup',
  sessionDates: 'sessionDates',
  tagIds: 'tags',
};

const SORT_FIELD_MAP: Record<string, string> = {
  [EFamilyFilterParams.FIRST_NAME]: 'FIRST_NAME',
  [EFamilyFilterParams.LAST_NAME]: 'LAST_NAME',
};

export const buildFamilyExportBody = ({
  filters,
  sortColumn,
  visibleColumnKeys,
  resourceIds,
  isActiveOnly,
}: {
  filters?: Record<string, FilterValue | null>;
  sortColumn?: TSortParams;
  visibleColumnKeys?: string[];
  resourceIds?: string[];
  isActiveOnly?: boolean;
}): TFamilyExportRequest => {
  const columns = visibleColumnKeys
    ?.map(key => COLUMN_KEY_TO_EXPORT_ID[key])
    .filter(Boolean);

  return {
    ...(columns?.length && { columns }),
    filters: {
      ...(resourceIds?.length && { ids: resourceIds }),
      ...(filters?.firstName && {
        firstName: String(filters.firstName).trim(),
      }),
      ...(filters?.lastName && { lastName: String(filters.lastName).trim() }),
      ...(filters?.email && { email: String(filters.email).trim() }),
      ...(filters?.phone && { phone: String(filters.phone).trim() }),
      ...(filters?.locationIds && {
        locations: filters.locationIds as string[],
      }),
      ...(filters?.workshopTypes && {
        workshopTypes: filters.workshopTypes as string[],
      }),
      ...(filters?.ageGroups && {
        ageGroups: filters.ageGroups as EAgeGroup[],
      }),
      ...(filters?.tagIds && { tags: filters.tagIds as string[] }),
      ...(isActiveOnly && { isActiveOnly }),
    },
    ...(sortColumn?.sortField &&
      sortColumn?.sortDirection && {
        sort: {
          field: (SORT_FIELD_MAP[sortColumn.sortField] ||
            sortColumn.sortField) as TFamilyExportSort['field'],
          direction: sortColumn.sortDirection as TFamilyExportSort['direction'],
        },
      }),
  };
};
